import { undo, redo, history } from 'prosemirror-history';
import { Schema, Node } from 'prosemirror-model';
import { EditorState } from 'prosemirror-state';
import { EditorView, DirectEditorProps } from 'prosemirror-view';
import { chainCommands, joinBackward, selectNodeBackward, joinForward, selectNodeForward } from 'prosemirror-commands';
import { keymap } from 'prosemirror-keymap';
import { TextEditorSchemaSpecification } from './schema/texteditor-schema';
import { documentEventHandlerPlugin, buildShortcutKeyMap } from './event/event-handler';
import { ContentTransformer } from './transformer/content-tranformer';
import { SelectEvent } from './event/select-event';
import { EditorManagerConfiguration, FunctionSubscriber } from './configurations/editor-manager-configuration';
import { EditorContentConfiguration } from './configurations/editor-content';
import { ContentChangedEvent } from './event/text-changed-event';
import { deleteSelection } from './command/delete-selection';
import { addListNodes, wrapInList, liftListItem } from "prosemirror-schema-list";
import { ShortcutEvent } from "./event/shortcut-event";

export class EditorManager {
    private proseMirrorView : EditorView;
    private configuration : EditorManagerConfiguration;
    private currentContentConfiguration : EditorContentConfiguration;
    private initialContentConfiguration : EditorContentConfiguration;
    public undoAvailable = false;
    public redoAvailable = false;
    private schema : Schema<any, any>;
    private textEditorTransformer;

    constructor (generalConfiguration : EditorManagerConfiguration, contentConfiguration : EditorContentConfiguration) {
        this.configuration = generalConfiguration;
        this.initialContentConfiguration = contentConfiguration;
        this.currentContentConfiguration = Object.assign({}, contentConfiguration);
        this.schema = new Schema(new TextEditorSchemaSpecification(this.currentContentConfiguration));
        this.schema = new Schema({
            nodes: addListNodes(this.schema.spec.nodes, 'paragraph*', 'block'),
            marks: this.schema.spec.marks
        });
        this.textEditorTransformer = new ContentTransformer(this.schema);
    }

    private contentChangedSubscriber = (event : ContentChangedEvent) => {
        this.redoAvailable = event.undoCount > 0;
        this.undoAvailable = event.redoCount > 0;

        // Check if editorContentTextChangedSubscriber has a 'next' method (indicating it's a Subscriber)
        const subscriber = this.configuration.editorContentTextChangedSubscriber;
        if (subscriber) {
            if (typeof subscriber === 'function') {
                subscriber(event); // Call the function directly
            } else if ('next' in subscriber) {
                subscriber.next(event); // Call the next method if it's a Subscriber
            }
        }
    };

    buildPlugins () {
        return [
            history(),
            keymap({ 'Mod-z': undo, 'Mod-y': redo }),
            keymap(buildShortcutKeyMap(this.configuration.shortcutEventSubscriber as FunctionSubscriber<ShortcutEvent>, this.configuration.shortcutList)),
            keymap({ 'Backspace': chainCommands(deleteSelection, joinBackward, selectNodeBackward) }),
            keymap({ 'Delete': chainCommands(deleteSelection, joinForward, selectNodeForward) }),
            documentEventHandlerPlugin(
                this.contentChangedSubscriber, // Pass content change handler directly
                this.configuration.selectionEventSubscriber as FunctionSubscriber<SelectEvent>            )
        ];
    }

    initEditorState () : EditorState {
        const doc = this.textEditorTransformer.createStartDoc(this.initialContentConfiguration);
        const plugins = this.buildPlugins();
        return EditorState.create({ schema: this.schema, doc, plugins });
    }

    init (domNode : Node) {
        const editorState = this.initEditorState();
        const editorProperties : DirectEditorProps = {
            state: editorState,
            handleClick: () => {
                // Handling the editorTouchedSubscriber as either a function or a Subscriber
                const subscriber = this.configuration.editorTouchedSubscriber;
                if (subscriber) {
                    if (typeof subscriber === 'function') {
                        subscriber(true);
                    } else if ('next' in subscriber) {
                        subscriber.next(true);
                    }
                }
                return false;
            }
        };
        this.proseMirrorView = new EditorView(<any> domNode, editorProperties);
    }

    public close () {
        this.proseMirrorView.destroy();
    }

    reset () {
        if (this.proseMirrorView != null) {
            const editorState = this.initEditorState();
            this.proseMirrorView.state = editorState;
            this.undoAvailable = false;
            this.redoAvailable = false;
        }
    }

    /* sostituisce il contenuto del documento*/
    replaceContent (newEditorContent, includeInHistory : boolean) {
        this.textEditorTransformer.replaceContent(newEditorContent, this.proseMirrorView, includeInHistory);
    }

    setDisabled (isDisabled : boolean) {
        const newProps = this.proseMirrorView.props;
        newProps.editable = (() => !isDisabled);
        this.proseMirrorView.update(newProps);
    }

    replaceCurrentParagraph (text : string, cursorNextParagraph : boolean) {
        this.textEditorTransformer.replaceCurrentParagraph(text, cursorNextParagraph, this.proseMirrorView);
    }

    insertWithinCurrentParagraph (filter : string, text : string, cursorNextParagraph : boolean) {
        this.textEditorTransformer.insertWithinCurrentParagraph(filter, text, cursorNextParagraph, this.proseMirrorView);
    }

    setSelectedTextStyle (style : string, apply : boolean) : void {
        if (apply) {
            this.textEditorTransformer.addMark(this.proseMirrorView, style);
        } else {
            this.textEditorTransformer.removeMark(this.proseMirrorView, style);
        }
        this.focus();
    }

    clear () : void {
        this.currentContentConfiguration = Object.assign({}, this.initialContentConfiguration);
        this.textEditorTransformer.clear(this.initialContentConfiguration, this.proseMirrorView);
        this.redoAvailable = false;
        this.focus();
    }

    appendText (text : string) {
        this.textEditorTransformer.appendText(text, this.proseMirrorView);
    }

    replaceText (text : string) {
        this.textEditorTransformer.replaceText(text, this.proseMirrorView);
    }

    focus () {
        this.proseMirrorView.focus();
    }

    undo () : void {
        undo(this.proseMirrorView.state, this.proseMirrorView.dispatch);
    }

    redo () {
        redo(this.proseMirrorView.state, this.proseMirrorView.dispatch);
    }

    updateDocumentAttribute (attribute : any) {
        this.textEditorTransformer.updateDocumentAttribute(this.proseMirrorView, attribute);
        this.focus();
    }

    updateParagraphAttribute (contentConfiguration : EditorContentConfiguration) {
        this.textEditorTransformer.updateCurrentParagraph(contentConfiguration, this.proseMirrorView);
        this.focus();
    }

    getSelectedText () : SelectEvent {
        const textSelectedInfo : SelectEvent = new SelectEvent();
        const state = this.proseMirrorView.state;
        if (state.selection.from !== state.selection.to) {
            textSelectedInfo.startPos = state.selection.from;
            textSelectedInfo.endPos = state.selection.to;
            textSelectedInfo.text = state.doc.textBetween(state.selection.from, state.selection.to, ' ');
        }
        return textSelectedInfo;
    }

    splitParagraph () {
        this.textEditorTransformer.splitCurrentParagraph(this.proseMirrorView);
    }

    getListType () {
        let listType = '';
        const isInList = !wrapInList(this.proseMirrorView.state.schema.nodes.bullet_list)(this.proseMirrorView.state);
        if (isInList) {
            switch (this.proseMirrorView.state.selection.$from.node(2).type.name) {
                case 'bullet_list':
                    listType = 'unordered';
                    break;
                case 'ordered_list':
                    listType = 'ordered';
                    break;
            }
        }
        return listType;
    }

    setUnorderedListItem () {
        const state = this.proseMirrorView.state;
        liftListItem(state.schema.nodes.list_item)(this.proseMirrorView.state, this.proseMirrorView.dispatch);
        wrapInList(state.schema.nodes.bullet_list)(this.proseMirrorView.state, this.proseMirrorView.dispatch);
    }

    setOrderedListItem () {
        const state = this.proseMirrorView.state;
        liftListItem(state.schema.nodes.list_item)(this.proseMirrorView.state, this.proseMirrorView.dispatch);
        wrapInList(state.schema.nodes.ordered_list)(this.proseMirrorView.state, this.proseMirrorView.dispatch);
    }

    unsetListItem () {
        const state = this.proseMirrorView.state;
        liftListItem(state.schema.nodes.list_item)(state, this.proseMirrorView.dispatch);
    }
}
